import { getToolName, isToolUIPart, type UIMessage } from "ai";

export type ContractTypeProposalInput = {
  name?: string;
  value?: string;
  guidelines?: string;
  id?: string;
};

export type ToolCallApprovalPayload = {
  runId: string;
  toolCallId: string;
  toolName: string;
  args: ContractTypeProposalInput;
};

export type ContractTypeToolOutput = {
  success?: boolean;
  contractType?: {
    id: string;
    name: string;
    value: string;
    guidelines?: string | null;
  };
  error?: string;
};

export const WRITE_TOOL_NAMES = new Set([
  "createContractType",
  "updateContractType",
  "create-contract-type",
  "update-contract-type",
]);

export function isWriteToolName(toolName: string): boolean {
  return WRITE_TOOL_NAMES.has(toolName);
}

/** Latest successful write tool result in the message list (for list sync / highlight). */
export function findSuccessfulContractTypeWrite(
  messages: UIMessage[]
): { toolCallId: string; contractTypeId: string; name: string } | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role !== "assistant") continue;
    for (let j = message.parts.length - 1; j >= 0; j--) {
      const part = message.parts[j];
      if (!isToolUIPart(part)) continue;
      if (!isWriteToolName(getToolName(part))) continue;
      if (part.state !== "output-available") continue;
      const output = part.output as ContractTypeToolOutput | undefined;
      if (output?.success !== true || !output.contractType?.id) continue;
      return {
        toolCallId: part.toolCallId,
        contractTypeId: output.contractType.id,
        name: output.contractType.name ?? "",
      };
    }
  }
  return null;
}

export function normalizeProposalInput(raw: unknown): ContractTypeProposalInput {
  if (!raw || typeof raw !== "object") return {};
  const o = raw as Record<string, unknown>;
  return {
    id: typeof o.id === "string" ? o.id : undefined,
    name: typeof o.name === "string" ? o.name : undefined,
    value: typeof o.value === "string" ? o.value : undefined,
    guidelines: typeof o.guidelines === "string" ? o.guidelines : undefined,
  };
}
